import { Title } from 'src/onboarding/components/typography/Title/Title';
import styles from '../../styles/style.module.scss';
import { t } from '@web-solutions/module-localization';
import { useStore } from 'src/onboarding/store/store';
import tashaImg from '../../../../assets/images/tashaWithoutFace.png';
import sophiaImg from '../../../../assets/images/sophiaWithoutFace.png';
import isabellaImg from '../../../../assets/images/IsabellaWithotFace.png';
import jamesImg from '../../../../assets/images/james.webp';
import danielImg from '../../../../assets/images/daniel.webp';
import michaelImg from '../../../../assets/images/michael.webp';
import { ReactComponent as StarSvg } from '../../../../assets/images/star.svg';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import dnaReviewMaria from '../../../../assets/images/card-dna-01.png';
import dnaReviewDaniel from '../../../../assets/images/card-dna-02.png';
import dnaReviewSophie from '../../../../assets/images/card-dna-03.png';
import getLocale from 'src/localization/get-locale';
import { LocalizedString } from 'core/constants/remote-config';

interface Customer {
  img: string;
  name: string;
  descriptions: string;
  weightLeft?: number;
  weightRight?: number;
}

interface ReviewCustomer {
  image: string;
  title: string;
  text: string | LocalizedString;
}

const imageMap = {
  tashaImg: tashaImg,
  sophiaImg: sophiaImg,
  isabellaImg: isabellaImg,
  jamesImg: jamesImg,
  danielImg: danielImg,
  michaelImg: michaelImg,
  dnaReviewMaria: dnaReviewMaria,
  dnaReviewDaniel: dnaReviewDaniel,
  dnaReviewSophie: dnaReviewSophie,
};

export const CustomersComponent = () => {
  const gender = useStore((state) => state.gender);
  const { customersReview } = useRemoteConfig();
  const locale = getLocale();

  const defaultCustomers: Customer[] = [
    {
      img: tashaImg,
      name: t('onboarding.personalPlan.customers.1.name'),
      descriptions: t('onboarding.personalPlan.customers.1.descriptions'),
      weightLeft: 75,
      weightRight: 60,
    },
    {
      img: sophiaImg,
      name: t('onboarding.personalPlan.customers.2.name'),
      descriptions: t('onboarding.personalPlan.customers.2.descriptions'),
      weightLeft: 80,
      weightRight: 60,
    },
    {
      img: isabellaImg,
      name: t('onboarding.personalPlan.customers.3.name'),
      descriptions: t('onboarding.personalPlan.customers.3.descriptions'),
      weightLeft: 90,
      weightRight: 55,
    },
  ];

  const defaultCustomersMen: Customer[] = [
    {
      img: jamesImg,
      name: t('onboarding.personalPlan.customersMen.1.name'),
      descriptions: t('onboarding.personalPlan.customersMen.1.description'),
      weightLeft: 128,
      weightRight: 96,
    },
    {
      img: danielImg,
      name: t('onboarding.personalPlan.customersMen.2.name'),
      descriptions: t('onboarding.personalPlan.customersMen.2.description'),
      weightLeft: 107,
      weightRight: 85,
    },
    {
      img: michaelImg,
      name: t('onboarding.personalPlan.customersMen.3.name'),
      descriptions: t('onboarding.personalPlan.customersMen.3.description'),
      weightLeft: 124,
      weightRight: 97,
    },
  ];

  const getCustomersFromConfig = (): Customer[] => {
    if (!customersReview) {
      return gender === 'Male' ? defaultCustomersMen : defaultCustomers;
    }

    const reviewsArray = Array.isArray(customersReview) ? customersReview : [customersReview];

    if (reviewsArray.length === 0) {
      return gender === 'Male' ? defaultCustomersMen : defaultCustomers;
    }

    const validReviews = reviewsArray.filter(
      (review: ReviewCustomer) =>
        review &&
        review.title &&
        review.text &&
        review.title.trim() !== '' &&
        (typeof review.text === 'string'
          ? review.text.trim() !== ''
          : Object.values(review.text).some(text => text?.trim() !== ''))
    );

    if (validReviews.length === 0) {
      return gender === 'Male' ? defaultCustomersMen : defaultCustomers;
    }

    return validReviews.map((review: ReviewCustomer) => ({
      img: imageMap[review.image as keyof typeof imageMap] || dnaReviewMaria,
      name: review.title,
      descriptions: review.text[locale],
    }));
  };

  const customersToShow = getCustomersFromConfig();

  return (
    <>
      <div className={styles.customers}>
        <Title tag='h4' className={styles.customerTitle}>
          {t('onboarding.personalPlan.customersReviews')}
        </Title>
        {customersToShow.map(({ img, name, descriptions, weightLeft, weightRight }, index) => (
          <div className={styles.customer} key={index}>
            <div className={styles.customerImgContainer}>
              <img className={styles.customerImg} src={img} />
              {weightLeft && <span className={styles.weightLeft}>{weightLeft} kg</span>}
              {weightRight && <span className={styles.weightRight}>{weightRight} kg</span>}
            </div>

            <div className={styles.customerTop}>
              <span className={styles.customerName}>{name}</span>
              <span className={styles.customerRating}>
                {Array.from({ length: 5 }, (_, i) => (
                  <StarSvg key={i} />
                ))}
              </span>
            </div>
            <div className={styles.customerBody}>{descriptions}</div>
          </div>
        ))}
      </div>
    </>
  );
};
